import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RequestError } from '../../../interfaces/error.interface';
import { ReclassifyResponse } from '../interfaces/post-reclassify';
import {
    LightweightProductStorage,
    ReclassifyFilters,
    GetArticlesForReclassifyResponse
} from '../interfaces/get-articles-for-reclassify';
import { ProductState } from '../../../interfaces/product-state.interface';

export interface ReclassifyState {
    selectedArticles: number[];
    reclassifyLoading: boolean;
    error: RequestError | null;
    lastReclassifyResult: ReclassifyResponse | null;

    // Gestión de artículos
    articles: LightweightProductStorage[];
    articlesLoading: boolean;
    pagination: {
        page: number;
        size: number;
        total_elements: number;
    } | null;
    filters: ReclassifyFilters;

    // Estados de productos
    productStates: ProductState[];
    productStatesLoading: boolean;
}

const initialState: ReclassifyState = {
    selectedArticles: [],
    reclassifyLoading: false,
    error: null,
    lastReclassifyResult: null,

    // Gestión de artículos
    articles: [],
    articlesLoading: false,
    pagination: null,
    filters: {},

    // Estados de productos
    productStates: [],
    productStatesLoading: false,
};

const reclassifySlice = createSlice({
    name: 'reclassify',
    initialState,
    reducers: {
        // Reclasificar artículos
        reclassifyInitAction: (state) => {
            state.reclassifyLoading = true;
            state.error = null;
            state.lastReclassifyResult = null;
        },
        reclassifySuccessAction: (state, action: PayloadAction<ReclassifyResponse>) => {
            state.reclassifyLoading = false;
            state.lastReclassifyResult = action.payload;
            state.selectedArticles = [];
        },
        reclassifyFailureAction: (state, action: PayloadAction<RequestError>) => {
            state.reclassifyLoading = false;
            state.error = action.payload;
        },

        // Gestión de selección de artículos
        selectArticleAction: (state, action: PayloadAction<number>) => {
            if (!state.selectedArticles.includes(action.payload)) {
                state.selectedArticles.push(action.payload);
            }
        },
        unselectArticleAction: (state, action: PayloadAction<number>) => {
            state.selectedArticles = state.selectedArticles.filter(id => id !== action.payload);
        },
        setSelectedArticlesAction: (state, action: PayloadAction<number[]>) => {
            state.selectedArticles = action.payload;
        },
        clearSelectionAction: (state) => {
            state.selectedArticles = [];
        },

        // Obtener artículos
        getArticlesInitAction: (state) => {
            state.articlesLoading = true;
            state.articles = []; // Limpiar artículos anteriores
            state.error = null;
        },
        getArticlesSuccessAction: (state, action: PayloadAction<GetArticlesForReclassifyResponse>) => {
            state.articlesLoading = false;
            state.articles = action.payload.lightweight_product_storages;
            state.pagination = action.payload.pagination;
        },
        getArticlesFailureAction: (state, action: PayloadAction<RequestError>) => {
            state.articlesLoading = false;
            state.error = action.payload;
        },

        // Gestión de filtros
        setFiltersAction: (state, action: PayloadAction<ReclassifyFilters>) => {
            state.filters = action.payload;
        },

        // Estados de productos
        getProductStatesInitAction: (state) => {
            state.productStatesLoading = true;
            state.error = null;
        },
        getProductStatesSuccessAction: (state, action: PayloadAction<ProductState[]>) => {
            state.productStatesLoading = false;
            state.productStates = action.payload;
        },
        getProductStatesFailureAction: (state, action: PayloadAction<RequestError>) => {
            state.productStatesLoading = false;
            state.error = action.payload;
        },

        // Limpiar estado
        clearReclassifyAction: (state) => {
            state.selectedArticles = [];
            state.reclassifyLoading = false;
            state.error = null;
            state.lastReclassifyResult = null;
            state.articles = [];
            state.articlesLoading = false;
            state.pagination = null;
            state.filters = {};
        },
    },
});

export const {
    reclassifyInitAction,
    reclassifySuccessAction,
    reclassifyFailureAction,
    selectArticleAction,
    unselectArticleAction,
    setSelectedArticlesAction,
    clearSelectionAction,
    clearReclassifyAction,
    getArticlesInitAction,
    getArticlesSuccessAction,
    getArticlesFailureAction,
    setFiltersAction,
    getProductStatesInitAction,
    getProductStatesSuccessAction,
    getProductStatesFailureAction,
} = reclassifySlice.actions;

export default reclassifySlice.reducer; 